import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithFallback } from '../../services/apiClient';
import ReCAPTCHA from "react-google-recaptcha";
import styled from 'styled-components';
import bgLogin from '../../assets/backgrounds/banner-entrar-cadastrar.png'
import bgRegister from '../../assets/backgrounds/banner-entrar-cadastrar@2x.png'
import googleIcon from '../../assets/icons/google-icon.svg';
import facebookIcon from '../../assets/icons/facebook-icon.svg';
import appleIcon from '../../assets/icons/apple-icon.svg';

// Helpers GIS (Google Identity Services) fora do componente
let googleScriptPromise = null;
const LoadGoogleScript = () => {
  if (googleScriptPromise) return googleScriptPromise;
  googleScriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Falha ao carregar Google Identity'));
    document.body.appendChild(s);
  });
  return googleScriptPromise;
};

// Facebook SDK loader
let fbScriptPromise = null;
const loadFacebookSDK = (appId) => {
  if (fbScriptPromise) return fbScriptPromise;
  fbScriptPromise = new Promise((resolve, reject) => {
    if (window.FB) return resolve();
    window.fbAsyncInit = function() {
      window.FB.init({ appId, cookie: true, xfbml: false, version: 'v18.0' });
      resolve();
    };
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('Falha ao carregar Facebook SDK'));
    document.body.appendChild(s);
  });
  return fbScriptPromise;
};

const decodeSubFromJwt = (jwt) => {
  try {
    const [, payload] = jwt.split('.');
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const data = JSON.parse(json);
    return data?.sub || null;
  } catch (_) {
    return null;
  }
};
 
// Alias global para compatibilidade com código legado que referencia `apiUrl`
try { if (typeof window !== 'undefined' && !window.apiUrl) { window.apiUrl = import.meta.env.VITE_URL_API; } } catch(e) { console.error(e); }

// Base da API com fallback para produção e filtro de localhost
const ENV_API = String(import.meta.env?.VITE_URL_API || '').trim();
const DEV_MODE = String(import.meta.env?.VITE_DEV_MODE || 'false').toLowerCase() === 'true';
const IS_LOCAL = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(ENV_API);
const API_BASE = ENV_API || 'https://servicesapi.joviplanet.com/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
  @media (max-width: 480px) {
    padding: 0; /* full-screen no mobile */
  }
`;

// Wrapper para ajustar o tamanho do reCAPTCHA em telas pequenas e evitar overflow horizontal
const CaptchaWrapper = styled.div`
  @media (max-width: 380px) {
    transform: scale(0.9);
    transform-origin: 0 0;
  }
`;

const ModalContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  max-width: 800px;
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 420px;
    border-radius: 10px;
  }
  /* Full-screen experience em mobile pequeno */
  @media (max-width: 480px) {
    max-width: 100vw;
    width: 100vw;
    height: 100vh;
    border-radius: 0;
  }
  @media (max-width: 375px) {
    max-width: 92vw;
    border-radius: 8px;
  }
`;

const ImageSection = styled.div`
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  position: relative;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: white;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7));
  }

  h2 {
    position: relative;
    z-index: 1;
    font-size: 2rem;
    margin: 0;
    font-weight: 600;
  }

  p {
    position: relative;
    z-index: 1;
    margin: 8px 0 0;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const FormSection = styled.div`
  padding: 40px;
  overflow-y: auto;
  max-height: 90vh;

  @media (max-width: 768px) {
    padding: 24px;
  }
  @media (max-width: 375px) {
    padding: 16px;
  }
  @media (max-width: 480px) {
    height: 100vh;
    max-height: 100vh;
    padding: 20px 16px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #666;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
  @media (max-width: 480px) {
    position: fixed; /* manter sempre visível no full-screen */
    top: calc(env(safe-area-inset-top, 0px) + 8px);
    right: calc(env(safe-area-inset-right, 0px) + 8px);
    background: rgba(255,255,255,0.85);
    backdrop-filter: blur(4px);
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #333;
  margin: 0 0 24px;
  text-align: center;
  @media (max-width: 375px) {
    font-size: 1.25rem;
  }
`;

const SocialButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 24px;
  align-items: stretch;
  @media (max-width: 420px) {
    gap: 6px;
  }
`;

const SocialButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  color: #333;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f8f8;
    border-color: #BA824F;
  }

  img {
    width: 18px;
    height: 18px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    &:hover {
      background: white;
      border-color: #ddd;
    }
  }
  @media (max-width: 420px) {
    padding: 8px;
    img { width: 16px; height: 16px; }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
  color: #666;
  font-size: 0.875rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #ddd;
  }

  span {
    padding: 0 12px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  position: relative;

  input, select {
    width: 100%;
    padding: 12px;
    padding-right: 45px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 0.875rem;
    transition: all 0.2s;
    @media (max-width: 375px) {
      padding: 10px 12px;
      font-size: 0.85rem;
    }

    &:focus {
      outline: none;
      border-color: #BA824F;
      box-shadow: 0 0 0 2px rgba(186, 130, 79, 0.1);
    }

    &::placeholder {
      color: #999;
    }

    &:disabled {
      background: #f5f5f5;
      cursor: not-allowed;
    }
  }

  .password-toggle {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    color: #666;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: color 0.2s;
    @media (max-width: 375px) {
      right: 10px;
    }

    &:hover {
      color: #BA824F;
    }
  }

  .error-message {
    position: absolute;
    bottom: -18px;
    left: 0;
    font-size: 0.75rem;
    color: #dc3545;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    transition: all 0.2s;

    &:hover {
      border-color: #BA824F;
    }

    input[type="radio"] {
      accent-color: #BA824F;
    }
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  background: #BA824F;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: #a77445;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .loading-spinner {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: translateY(-50%) rotate(360deg);
    }
  }
`;

// Botão de atalho para Cadastro Hostd (estilo do CTA marrom)
const HostdCTA = styled.a`
  display: block;
  background: #BA824F;
  color: #fff !important;
  text-align: center;
  font-weight: 600;
  padding: 10px 14px;
  border-radius: 10px;
  text-decoration: none !important;
  width: 100%;
  max-width: 260px;
  margin: 0 auto;
  transition: filter .2s ease;

  &:hover {
    filter: brightness(0.96);
    text-decoration: none !important;
  }
`;

const BottomText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #666;
  margin: 16px 0 0;

  a {
    color: #BA824F;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  color: #c53030;
  font-size: 0.875rem;
  margin-bottom: 16px;
`;

// Certifique-se de que NENHUM hook está fora do componente!

const LoginModal = ({ onClose, activeTab, setUser }) => {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState(activeTab || 'login'); // Use activeTab as the initial view

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userType: 'individual', // Padrão ajustado para 'individual' (particular)
    name: '',
    sobrenome: '',
    confirmPassword: '',
    termosCondicoes: false, // Adicionado para evitar erros de undefined
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [socialLoginDisabled, setSocialLoginDisabled] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [recoveryEmailSent, setRecoveryEmailSent] = useState(false);

  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    sobrenome: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Login social (Google pronto; FB/Apple aguardam SDK)
  const handleSocialLogin = async (provider) => {
    try {
      setSocialLoginDisabled(true);
      let providerId = '';
      let socialToken = '';

      if (provider === 'google') {
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID_LOGIN || import.meta.env.VITE_GOOGLE_CLIENT_ID;
        if (!clientId) throw new Error('Defina VITE_GOOGLE_CLIENT_ID_LOGIN no .env');
        setGoogleLoading(true);
        await LoadGoogleScript();
        if (!window.google?.accounts?.id) throw new Error('Google Identity indisponível');

        socialToken = await new Promise((resolve, reject) => {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (resp) => resp?.credential ? resolve(resp.credential) : reject(new Error('Não foi possível obter o credential Google'))
          });
          window.google.accounts.id.prompt((notif) => {
            if (notif.isNotDisplayed() || notif.isSkippedMoment()) {
              reject(new Error('Login Google cancelado ou não exibido.'));
            }
          });
        });
        providerId = decodeSubFromJwt(socialToken) || '';
        if (!providerId) throw new Error('ID Google não lido.');
      } else if (provider === 'facebook') {
        const appId = import.meta.env.VITE_FACEBOOK_APP_ID;
        if (!appId) throw new Error('Defina VITE_FACEBOOK_APP_ID no .env');
        if (window.location.protocol !== 'https:') {
          throw new Error('Facebook Login exige página em HTTPS. Use https://localhost ou domínio https.');
        }
        await loadFacebookSDK(appId);
        await new Promise((resolve, reject) => {
          window.FB.login(async (response) => {
            if (response.authResponse) {
              socialToken = response.authResponse.accessToken;
              providerId = response.authResponse.userID;
              resolve();
            } else {
              reject(new Error('Login Facebook cancelado ou falhou.'));
            }
          }, { scope: 'email' });
        });
        if (!providerId) throw new Error('ID Facebook não lido.');
      } else {
        // Placeholder para Apple até integrar SDK
        providerId = window.prompt(`Informe o ID do ${provider}`) || '';
        if (!providerId) return;
      }

      const resp = await fetchWithFallback('/usuarios/social-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json'
        },
        body: JSON.stringify({ provider, providerId, token: socialToken })
      });

      const raw = await resp.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch (_) { data = {}; }

      if (!resp.ok) {
        const msg = data?.mensagem || data?.message || data?.error || raw || 'Falha no login social';
        throw new Error(msg);
      }

      const token = data?.token || data?.auth_token || data?.access_token;
      const userData = data?.usuario;
      if (token) localStorage.setItem('auth_token', token);
      if (userData) localStorage.setItem('user_data', JSON.stringify(userData));
      if (typeof setUser === 'function' && userData) setUser(userData);
      if (onClose) onClose();
      try { window.location.reload(); } catch (e) { console.error(e); }
    } catch (err) {
      console.error('Login social erro:', err);
      setError(err.message || 'Erro ao fazer login social');
    } finally {
      if (provider === 'google') setGoogleLoading(false);
      setSocialLoginDisabled(false);
    }
  };


  // Evita rolagem do body quando o modal está aberto (melhor UX em mobile)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, []);

  const handleRecover = async () => {
    const email = (formData.email || '').trim();
    if (!email) {
      throw new Error('Informe o e-mail para recuperar a senha.');
    }
    const payload = { emailUSUARIO: email };
    const url = '/api/usuarios/recuperar-senha';

    try {
      const resp = await fetchWithFallback(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Erro ${resp.status}`);
      }
      return true;
    } catch (err) {
      console.error('[login-modal] Erro ao enviar e-mail de recuperação', err);
      throw err || new Error('Não foi possível enviar o e-mail de recuperação.');
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const validateForm = () => {
    const errors = {};
    
    if (!validateEmail(formData.email)) {
      errors.email = 'Email inválido';
    }

    if (currentView !== 'recover' && !validatePassword(formData.password)) {
      errors.password = 'A senha deve ter pelo menos 8 caracteres';
    }

    if (currentView === 'register' && !formData.name.trim()) {
      errors.name = 'Nome obrigatório';
    }
    if (currentView === 'register' && !formData.sobrenome.trim()) {
      errors.sobrenome = 'Sobrenome obrigatório';
    }
    if (currentView === 'register' && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    }
    if (currentView === 'register' && !formData.termosCondicoes) {
      errors.termosCondicoes = 'É necessário aceitar os Termos e Condições.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setSocialLoginDisabled(true);
    
    try {
      // Validação do formulário
      if (!validateForm()) {
        throw new Error('Por favor, preencha todos os campos corretamente.');
      }

      // Validação do reCAPTCHA para registro e login
      if (currentView !== 'recover' && !captchaValue) {
        throw new Error('Por favor, complete a validação do reCAPTCHA.');
      }

      // Executa a ação conforme a view atual
      if (currentView === 'login') {
        await handleLogin();
      } else if (currentView === 'register') {
        await handleRegister();
        // Se chegou aqui, o registro foi bem-sucedido
        const { default: Swal } = await import('sweetalert2');
        await Swal.fire({
          icon: 'success',
          title: 'Cadastro realizado com sucesso!',
          html: `
            <div style="text-align: left;">
              <p>Enviamos um e-mail para <strong>${formData.email}</strong> com as instruções para ativar sua conta.</p>
              <p>Verifique sua caixa de entrada e a pasta de spam.</p>
            </div>
          `,
          confirmButtonText: 'Entendi',
          confirmButtonColor: '#4CAF50',
          allowOutsideClick: false
        });
        
        // Limpa o formulário
        setFormData({
          email: formData.email, // Mantém o e-mail para facilitar o login
          password: '',
          userType: 'professional',
          name: '',
          sobrenome: '',
          confirmPassword: '',
          termosCondicoes: false
        });
        
        // Muda para a tela de login
        setCurrentView('login');
        
      } else if (currentView === 'recover') {
        await handleRecover();
        setRecoveryEmailSent(true);
      }
    } catch (err) {
      console.error('Erro ao processar requisição:', err);
      
      // Exibe mensagem de erro usando SweetAlert2 para erros importantes
      if (err.message.includes('já está sendo utilizado') || 
          err.message.includes('inválido') || 
          err.message.includes('incorretos')) {
        const { default: Swal } = await import('sweetalert2');
        await Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: err.message,
          confirmButtonText: 'Entendi'
        });
      } else {
        // Para outros erros, usa o estado de erro padrão
        setError(err.message || 'Ocorreu um erro inesperado. Por favor, tente novamente.');
      }
    } finally {
      setLoading(false);
      setSocialLoginDisabled(false);
    }
  };

  const handleLogin = async () => {
  const payload = { emailUSUARIO: formData.email, senhaUSUARIO: formData.password };
  const url = '/usuarios/login';
  const resp = await fetchWithFallback(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json'
    },
    body: JSON.stringify(payload)
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Erro ${resp.status}`);
  }
  const data = await resp.json();
  if (data?.token || data?.auth_token || data?.access_token) {
    const token = data.token || data.auth_token || data.access_token;
    localStorage.setItem('auth_token', token);
  }
  
  // Armazena dados do usuário
  const userData = data?.usuario;

  if (userData) {
    localStorage.setItem('user_data', JSON.stringify(userData));
  }
  
  // Atualiza contexto de autenticação se disponível
  if (typeof setUser === 'function' && userData) {
    setUser(userData);
  }
  
  // Fecha o modal
  if (onClose) {
    onClose();
  }

  // Recarrega a página para garantir hidratação completa do estado logado
  try { window.location.reload(); } catch (e) { console.error(e); }
};

const handleRegister = async () => {
  try {
    if (!formData.name || !formData.email || !formData.password || !formData.termosCondicoes) {
      throw new Error('Por favor, preencha todos os campos obrigatórios.');
    }
    if (formData.password !== formData.confirmPassword) {
      throw new Error('As senhas não coincidem. Por favor, verifique.');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Por favor, insira um endereço de e-mail válido.');
    }
    if (!captchaValue) {
      throw new Error('Por favor, complete a validação do reCAPTCHA.');
    }
    const url = `${API_BASE}/usuarios-particular/criar`;
    const payload = {
      nomeUSUARIO: formData.name.trim(),
      sobrenomeUSUARIO: (formData.sobrenome || '').trim(),
      emailUSUARIO: formData.email.trim(),
      senhaUSUARIO: formData.password,
      termoecondicoesUSUARIO: formData.termosCondicoes ? 1 : 0,
      facebookidUSUARIO: '',
      appleidUSUARIO: '',
      googleidUSUARIO: '',
      recaptcha: captchaValue
    };
    setLoading(true);
    setError(null);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json',
        'Accept-Language': 'pt-PT,pt;q=0.9',
        'Content-Language': 'pt-PT'
      },
      body: JSON.stringify(payload)
    });
    const responseText = await response.text();
    let data = null;
    try {
      data = responseText ? JSON.parse(responseText) : null;
    } catch (e) {
      console.error('Erro ao fazer parse da resposta:', e);
      throw new Error('Resposta inválida do servidor. Tente novamente.');
    }
    console.log('[REGISTER] Status da resposta:', response.status);
    console.log('[REGISTER] Corpo da resposta:', data || responseText);
    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('Este e-mail já está sendo utilizado. Por favor, utilize outro e-mail ou faça login.');
      }
      const errorMessage = data?.message || data?.error || 'Erro ao processar o cadastro. Tente novamente.';
      throw new Error(errorMessage);
    }
    setError(null);
  } catch (error) {
    console.error('Erro no registro:', error);
    throw error;
  }
};

const getViewTitle = () => {
  switch (currentView) {
    case 'login': return 'Login';
    case 'register': return '';
    case 'recover': return 'Recuperar Senha';
    default: return '';
  }
};

  const getViewImage = () => {
    switch (currentView) {
      case 'login': return bgRegister;
      case 'register': return bgLogin;
      case 'recover': return '/recover-bg.jpg';
      default: return '';
    }
  };

  const renderForm = () => {
    if (currentView === 'recover' && recoveryEmailSent) {
      return (
        <div style={{ textAlign: 'center' }}>
          <h3>Email Enviado!</h3>
          <p>Verifique a sua caixa de entrada para instruções de recuperação da senha.</p>
          <SubmitButton onClick={() => setCurrentView('login')}>
            Voltar para o Login
          </SubmitButton>
        </div>
      );
    }

    return (
      <Form onSubmit={handleSubmit}>
        {currentView === 'register' && (
        <>
            <InputGroup>
              <input
                type="text"
                name="name"
                placeholder={t('login.namePlaceholder', { defaultValue: 'Nome' })}
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
              {validationErrors.name && (
                <span className="error-message">{validationErrors.name}</span>
              )}
            </InputGroup>
            <InputGroup>
              <input
                type="text"
                name="sobrenome"
                placeholder={t('login.surnamePlaceholder', { defaultValue: 'Sobrenome' })}
                value={formData.sobrenome}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
              {validationErrors.sobrenome && (
                <span className="error-message">{validationErrors.sobrenome}</span>
              )}
            </InputGroup>

          {/* Seção 'Tipo de Usuário' ocultada por solicitação */}


          </>
        )}

        <InputGroup>
          <input
            type="email"
            name="email"
            placeholder={t('login.emailPlaceholder', { defaultValue: 'Digite seu email' })}
            value={formData.email}
            onChange={handleInputChange}
            required
            disabled={loading}
          />
          {validationErrors.email && (
            <span className="error-message">{validationErrors.email}</span>
          )}
        </InputGroup>

        {currentView !== 'recover' && (
          <InputGroup>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder={t('login.passwordPlaceholder', { defaultValue: 'Digite sua senha' })}
              value={formData.password}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? (
                // Olho aberto
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <ellipse cx="12" cy="12" rx="10" ry="7"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              ) : (
                // Olho fechado (com risco)
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-5 0-9.27-3.11-11-7.5a11.4 11.4 0 0 1 5.17-5.61"/>
                  <path d="M1 1l22 22"/>
                  <path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5a3.5 3.5 0 0 0 3.5-3.5c0-.67-.19-1.29-.53-1.82"/>
                </svg>
              )}
            </button>
            {validationErrors.password && (
              <span className="error-message">{validationErrors.password}</span>
            )}
          </InputGroup>
        )}

        {currentView === 'register' && (
          <>
            <InputGroup>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder={t('login.confirmPasswordPlaceholder', { defaultValue: 'Confirme sua senha' })}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}
                aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                title={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showConfirmPassword ? (
                  // Olho aberto
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <ellipse cx="12" cy="12" rx="10" ry="7"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                ) : (
                  // Olho fechado (com risco)
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-5 0-9.27-3.11-11-7.5a11.4 11.4 0 0 1 5.17-5.61"/>
                    <path d="M1 1l22 22"/>
                    <path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5a3.5 3.5 0 0 0 3.5-3.5c0-.67-.19-1.29-.53-1.82"/>
                  </svg>
                )}
              </button>
              {validationErrors.confirmPassword && (
                <span className="error-message">{validationErrors.confirmPassword}</span>
              )}
            </InputGroup>
            <InputGroup style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                name="termosCondicoes"
                checked={!!formData.termosCondicoes}
                onChange={e => setFormData(f => ({ ...f, termosCondicoes: e.target.checked }))}
                required
                disabled={loading}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontSize: 14 }}>
                Aceito os <a href="/termos" target="_blank" rel="noopener noreferrer">Termos e Condições</a>
              </span>
              {validationErrors.termosCondicoes && (
                <span className="error-message">{validationErrors.termosCondicoes}</span>
              )}
            </InputGroup>
          </>
        )}

        {currentView !== 'recover' && (
          <CaptchaWrapper>
            <ReCAPTCHA
              sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
              onChange={(value) => setCaptchaValue(value)}
            />
          </CaptchaWrapper>
        )}

        <SubmitButton type="submit" disabled={loading}>
          {loading ? (
            <>
              {currentView === 'login' && 'Entrando...'}
              {currentView === 'register' && 'Cadastrando...'}
              {currentView === 'recover' && 'Enviando...'}
              <div className="loading-spinner" />
            </>
          ) : (
            <>
              {currentView === 'login' && 'Entrar'}
              {currentView === 'register' && 'Cadastrar'}
              {currentView === 'recover' && 'Enviar Email de Recuperação'}
            </>
          )}
        </SubmitButton>

        <BottomText>
          {currentView === 'login' && (
            <>
              Esqueceu sua senha?{' '}
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setCurrentView('recover');
              }}>
                Recuperar senha
              </a>
              <br />
              <br />
              Não tem uma conta?{' '}
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setCurrentView('register');
              }}>
                Cadastre-se
              </a>
            </>
          )}
          {currentView === 'register' && (
            <>
              Já possui conta?{' '}
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setCurrentView('login');
              }}>
                Faça login
              </a>
            </>
          )}
          {currentView === 'recover' && (
            <>
              Lembrou sua senha?{' '}
              <a href="#" onClick={(e) => {
                e.preventDefault();
                setCurrentView('login');
              }}>
                Voltar para o login
              </a>
            </>
          )}
        </BottomText>
      </Form>
    );
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContainer>
        <ImageSection $image={getViewImage()}>
          <h2>
            {currentView === 'login' && ''}
            {currentView === 'register' && ''}
            {currentView === 'recover' && 'Recover password'}
          </h2>
          <p>
            {currentView === 'login' && 'Please log in'}
            {currentView === 'register' && 'Join our community'}
            {currentView === 'recover' && 'Get back to your account'}
          </p>
        </ImageSection>

        <FormSection>
          <CloseButton onClick={onClose}>&times;</CloseButton>
          {currentView === 'register' && (
            <div style={{ marginBottom: 16 }}>
              <HostdCTA
                href="/cadastro/hostd"
                onClick={(e) => {
                  e.preventDefault();
                  if (typeof onClose === 'function') onClose();
                  window.location.href = '/cadastro/hostd';
                }}
              >
                Cadastro de Anfitrião
              </HostdCTA>
            </div>
          )}
          {(() => { const t = getViewTitle(); return t ? <Title>{t}</Title> : null; })()}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {currentView !== 'recover' && (
            <>
              <div style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: 8, textAlign: 'center' }}>Continuar com</div>
              <SocialButtons>
                <SocialButton 
                  onClick={() => handleSocialLogin('google')}
                  disabled={socialLoginDisabled || googleLoading}
                  aria-label="Continuar com Google"
                  title="Continuar com Google"
                >
                  <img src={googleIcon} alt="Google" />
                </SocialButton>
                <SocialButton 
                  onClick={() => handleSocialLogin('facebook')}
                  disabled={socialLoginDisabled}
                  aria-label="Continuar com Facebook"
                  title="Continuar com Facebook"
                >
                  <img src={facebookIcon} alt="Facebook" />
                </SocialButton>
                <SocialButton 
                  onClick={() => handleSocialLogin('apple')}
                  disabled={socialLoginDisabled}
                  aria-label="Continuar com Apple"
                  title="Continuar com Apple"
                >
                  <img src={appleIcon} alt="Apple" />
                </SocialButton>
              </SocialButtons>

              <Divider>
                <span>ou</span>
              </Divider>
            </>
          )}

          {renderForm()}
        </FormSection>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default LoginModal;